import * as React from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'sonner';
import {
  ArrowUp,
  Info,
  Video,
  Loader,
  ScanText,
  Plus,
  Mic,
} from 'lucide-react';
import { Stop } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import {
  FileChip,
  FeatureToggleChip,
  FileSelector,
} from '@/features/chat/shared/components';
import { sanitizeMarkdown } from '@/features/chat/shared';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { Textarea } from '../../../components/ui/textarea';
import { Switch } from '../../../components/ui/switch';
import { TooltipProvider } from '../../../components/ui/tooltip';
import { ScrollArea } from '../../../components/ui/scroll-area';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../components/ui/popover';
import { FileType } from '../../../types';
import {
  askMultiFileSummarizedQuestion,
  askSingleFileQuestionStream,
} from '../../../services/multiFileAskAIService';
import { screenAppAPIClient } from '../../../lib/baseAxios';

import { FilePreviewRouteParams } from '../../selectedVideo/types';
import {
  setVideoMode,
  setOcrMode,
  addSelectedFile,
  removeSelectedFile,
  clearChatError,
  addUserMessageToChat,
  addAssistantMessageToChat,
  updateLastAssistantMessage,
  setChatStatus as setChStatus,
  setChatSessionId,
} from '../../selectedVideo/selectedVideoSlice';
import { RootState } from '../../../reducers';
import { useLimits } from '../../plan/hooks/limits';
import ActionDialog from '../../globalModals/ActionDialog';
import {
  useOutputLanguage,
  OUTPUT_LANGUAGES,
  getCurrentOutputLanguage,
} from '../../../hooks/useOutputLanguage';
import {
  trackAskAIChat,
  type AskAIChatType,
} from '../shared/chatTrackingActions';
import DictationService from '../../../services/dictationService';
import { AppDispatch } from '../../../store';

export interface ChatInputRef {
  focusInput: () => void;
}

const CustomizedInputBase = React.forwardRef<
  ChatInputRef,
  {
    noAudio?: boolean;
  }
>(({ noAudio }, ref) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const [question, setQuestion] = React.useState('');
  const [showLongVideoDialog, setShowLongVideoDialog] = React.useState(false);
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const [languageOpen, setLanguageOpen] = React.useState(false);
  const [infoDialogOpen, setInfoDialogOpen] = React.useState(false);
  const [infoDialogContent, setInfoDialogContent] = React.useState<{
    title: string;
    content: string;
    featureType: 'videoAnalysis' | 'ocr' | null;
  }>({ title: '', content: '', featureType: null });
  const [isFocused, setIsFocused] = React.useState(false);
  const [isMultiline, setIsMultiline] = React.useState(false);
  const [showFileSelector, setShowFileSelector] = React.useState(false);
  const [cachedFiles, setCachedFiles] = React.useState<FileType[]>([]);
  const [isRecordingLocal, setIsRecordingLocal] = React.useState(false);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const guardLimits = useLimits();

  // AbortController for canceling streaming requests
  const abortControllerRef = React.useRef<AbortController | null>(null);

  // Track streaming response for partial responses
  const streamingResponseRef = React.useRef<string>('');

  // Dictation refs
  const mediaRecorder = React.useRef<MediaRecorder | null>(null);
  const chunks = React.useRef<Blob[]>([]);
  const audioStream = React.useRef<MediaStream | null>(null);

  // Fully local dictation state - not shared via Redux
  const [isDictationProcessing, setIsDictationProcessing] =
    React.useState(false);

  const teamCustomVocabulary =
    useSelector(
      (state: RootState) =>
        state.team.selectedTeam?.fileOptions?.customVocabulary,
    ) || '';
  const showLimitReached = guardLimits('askAI') <= 0;
  const { fileId } = useParams<FilePreviewRouteParams>();
  const { status, sessionId, isVideoMode, ocrEnabled, selectedFiles } =
    useSelector((state: RootState) => state.selectedVideo.chat);
  const {
    streams,
    duration = 0,
    teamId,
  } = useSelector((state: RootState) => state.selectedVideo.file);
  const hasVideo = streams?.includes('video');
  const { outputLanguage, currentLanguageName, updateOutputLanguage } =
    useOutputLanguage();

  // Ref to hold the pending dictation transcript for auto-submit
  const pendingDictationRef = React.useRef<string | null>(null);

  // Reset textarea height when question is cleared (e.g., after submit)
  React.useEffect(() => {
    if (!question && textareaRef.current) {
      // Add transition for smooth height animation
      textareaRef.current.style.transition = 'height 150ms ease-out';
      textareaRef.current.style.height = 'auto';
      setIsMultiline(false);
      // Remove transition after animation completes to not interfere with typing
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.style.transition = '';
        }
      }, 150);
    }
  }, [question]);

  // Start batch dictation recording - fully local, no Redux
  const handleStartDictation = React.useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: 16000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });
      audioStream.current = stream;

      const options = {
        mimeType: 'audio/webm;codecs=opus',
        audioBitsPerSecond: 128000,
      };

      if (MediaRecorder.isTypeSupported(options.mimeType)) {
        mediaRecorder.current = new MediaRecorder(stream, options);
      } else {
        mediaRecorder.current = new MediaRecorder(stream);
      }

      mediaRecorder.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.current.push(event.data);
        }
      };

      mediaRecorder.current.onstop = async () => {
        const audioBlob = new Blob(chunks.current, {
          type: mediaRecorder.current?.mimeType || 'audio/webm;codecs=opus',
        });
        chunks.current = [];

        // Set processing state locally
        setIsDictationProcessing(true);

        try {
          const currentOutputLanguage = await getCurrentOutputLanguage();

          // Call the service directly - no Redux
          const transcript = await DictationService.processBatchAudioDictation(
            audioBlob,
            {
              mode: 'fast',
              customVocabulary: teamCustomVocabulary,
              outputLanguage: currentOutputLanguage,
            },
          );

          setIsDictationProcessing(false);

          if (transcript && transcript.trim()) {
            // Set transcript and trigger auto-submit
            pendingDictationRef.current = transcript;
            setQuestion(transcript);
          }
        } catch (err) {
          console.error('Failed to process dictation:', err);
          setIsDictationProcessing(false);
        }
      };

      mediaRecorder.current.start(1000);
      setIsRecordingLocal(true);
    } catch (err) {
      console.error('Failed to start dictation:', err);
    }
  }, [teamCustomVocabulary]);

  // Stop dictation recording
  const handleStopDictation = React.useCallback(() => {
    if (mediaRecorder.current && isRecordingLocal) {
      mediaRecorder.current.stop();
      if (audioStream.current) {
        audioStream.current.getTracks().forEach((track) => track.stop());
        audioStream.current = null;
      }
      setIsRecordingLocal(false);
    }
  }, [isRecordingLocal]);

  // Expose focusInput method to parent via ref
  React.useImperativeHandle(
    ref,
    () => ({
      focusInput: () => {
        textareaRef.current?.focus();
      },
    }),
    [],
  );

  const getPlaceholderText = () => {
    if (noAudio && !isVideoMode && !ocrEnabled)
      return t('chat.ask.placeholder.videoAnalyzer');
    if (!hasVideo) return t('chat.ask.placeholder.audio');
    if (isVideoMode) return t('chat.ask.placeholder.visualContent');
    if (ocrEnabled)
      return t(
        'chat.ask.placeholder.ocr',
        'Ask about text shown in your video...',
      );
    return t('chat.ask.placeholder.default');
  };

  const handleVideoModeToggle = () => {
    if (!isVideoMode) {
      // Cannot enable if files are selected
      if (selectedFiles.length > 0) {
        return;
      }
      // Check if video is longer than 50 minutes (3000 seconds)
      if (duration > 3000) {
        setShowLongVideoDialog(true);
        return;
      }
      guardLimits('VideoAnalyzer', () => {
        dispatch(setVideoMode(true));
      });
    } else {
      dispatch(setVideoMode(false));
    }
  };

  const handleOcrToggle = () => {
    if (!ocrEnabled) {
      // Cannot enable if files are selected
      if (selectedFiles.length > 0) {
        return;
      }
      // Check if video is longer than 50 minutes (3000 seconds)
      if (duration > 3000) {
        setShowLongVideoDialog(true);
        return;
      }
      guardLimits('VideoAnalyzer', () => {
        dispatch(setOcrMode(true));
      });
    } else {
      dispatch(setOcrMode(false));
    }
  };

  const handleFileSelect = (file: FileType) => {
    const fileIdToToggle = file._id;
    if (selectedFiles.includes(fileIdToToggle)) {
      dispatch(removeSelectedFile(fileIdToToggle));
      // Remove from cache
      setCachedFiles((current) =>
        current.filter((f) => f._id !== fileIdToToggle),
      );
    } else {
      dispatch(addSelectedFile(fileIdToToggle));
      // Add to cache
      setCachedFiles((current) => [...current, file]);
    }
  };

  // Fetch file data for selected files
  React.useEffect(() => {
    if (selectedFiles.length === 0) {
      setCachedFiles([]);
      return;
    }

    // Only fetch files that are not already in cache
    const missingFileIds = selectedFiles.filter(
      (selectedFileId) => !cachedFiles.find((f) => f._id === selectedFileId),
    );

    if (missingFileIds.length === 0) return;

    const fetchFilesData = async () => {
      try {
        const filePromises = missingFileIds.map(async (selectedFileId) => {
          try {
            const response = await (screenAppAPIClient as any).api.get(
              `/v2/files/${selectedFileId}`,
            );
            return (
              response.data?.data?.file || response.data?.file || response.data
            );
          } catch (error) {
            console.error(`Failed to fetch file ${selectedFileId}:`, error);
            return null;
          }
        });
        const filesData = await Promise.all(filePromises);
        const validFiles = filesData.filter(Boolean);
        setCachedFiles((current) => [...current, ...validFiles]);
      } catch (error) {
        console.error('Failed to fetch files data:', error);
      }
    };

    fetchFilesData();
  }, [selectedFiles]);

  const handleLanguageSelect = async (languageCode: string) => {
    await updateOutputLanguage(languageCode);
    setLanguageOpen(false);
  };

  const handleInfoClick = (
    event: React.MouseEvent,
    title: string,
    content: string,
    featureType: 'videoAnalysis' | 'ocr',
  ) => {
    event.stopPropagation();
    setInfoDialogContent({ title, content, featureType });
    setInfoDialogOpen(true);
  };

  const handleInfoDialogClose = () => {
    setInfoDialogOpen(false);
    setInfoDialogContent({ title: '', content: '', featureType: null });
  };

  const handleTurnOnFeature = () => {
    if (infoDialogContent.featureType === 'videoAnalysis') {
      handleVideoModeToggle();
    } else if (infoDialogContent.featureType === 'ocr') {
      handleOcrToggle();
    }
    handleInfoDialogClose();
  };

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const query = event.target.value;
    setQuestion(query);
  };

  // Helper to handle search with a specific question (for dictation auto-submit)
  const handleSearchWithQuestion = async (
    questionText: string,
    event: React.FormEvent | React.KeyboardEvent,
  ) => {
    event.preventDefault();
    if (!questionText) return;

    // Check if we should use multi-file ask AI
    const useMultiFile = selectedFiles.length > 0;

    // For multi-file, we need teamId and at least one file
    if (useMultiFile && (!teamId || selectedFiles.length === 0)) {
      toast.error('Please select at least one file to analyze');
      return;
    }

    // For single-file, we need fileId
    if (!useMultiFile && !fileId) {
      return;
    }

    if (showLimitReached) {
      guardLimits('askAI', () => {});
      return;
    }

    const currentQuestion = questionText.trim();
    setQuestion('');
    setIsMultiline(false);

    // Continue with the same logic as handleSearch...
    guardLimits('askAI', async () => {
      dispatch(clearChatError());

      let chatType: AskAIChatType;
      if (useMultiFile) {
        chatType = 'multifile';
      } else if (isVideoMode) {
        chatType = 'video';
      } else {
        chatType = 'search';
      }

      dispatch(
        trackAskAIChat({
          chatType,
          question: currentQuestion,
          fileCount: useMultiFile ? selectedFiles.length : 1,
          isVideoMode,
          ocrEnabled,
          sessionId,
          isNewSession: !sessionId,
        }),
      );

      if (useMultiFile) {
        dispatch(
          addUserMessageToChat({ role: 'user', content: currentQuestion }),
        );
        dispatch(setChStatus('pending'));

        try {
          const response = await askMultiFileSummarizedQuestion(teamId!, {
            fileIds: selectedFiles,
            question: currentQuestion,
            sessionId: sessionId || undefined,
          });

          if (response.success && response.data) {
            dispatch(
              addAssistantMessageToChat({
                role: 'assistant',
                content: response.data.answer.content,
              }),
            );
            dispatch(setChatSessionId(response.data.sessionId));
            dispatch(setChStatus('succeeded'));
          } else {
            throw new Error('Failed to get response');
          }
        } catch (error) {
          console.error('Failed to analyze recordings:', error);
          dispatch(setChStatus('failed'));
          toast.error(
            error instanceof Error
              ? error.message
              : 'Failed to process request. Please try again.',
          );
        }
      } else {
        if (!fileId || !teamId) return;

        dispatch(
          addUserMessageToChat({ role: 'user', content: currentQuestion }),
        );
        dispatch(setChStatus('pending'));
        dispatch(
          addAssistantMessageToChat({
            role: 'assistant',
            content: '',
          }),
        );

        streamingResponseRef.current = '';
        abortControllerRef.current = new AbortController();

        try {
          let mediaAnalysisOptions: any;
          if (isVideoMode) {
            mediaAnalysisOptions = { video: { segments: [] } };
          } else if (ocrEnabled) {
            mediaAnalysisOptions = { ocr: { enabled: true } };
          }

          const { sessionId: newSessionId } = await askSingleFileQuestionStream(
            teamId,
            fileId,
            currentQuestion,
            sessionId || undefined,
            (accumulatedText: string) => {
              streamingResponseRef.current = accumulatedText;
              dispatch(updateLastAssistantMessage(accumulatedText));
            },
            {
              signal: abortControllerRef.current.signal,
              mediaAnalysisOptions,
            },
          );

          abortControllerRef.current = null;
          const finalResponse = streamingResponseRef.current;
          streamingResponseRef.current = '';
          const sanitizedCompletion = sanitizeMarkdown(finalResponse);
          if (sanitizedCompletion !== finalResponse) {
            dispatch(updateLastAssistantMessage(sanitizedCompletion));
          }
          dispatch(setChatSessionId(newSessionId));
          dispatch(setChStatus('succeeded'));
        } catch (error) {
          console.error('Failed to analyze recording:', error);
          abortControllerRef.current = null;

          if (error instanceof Error && error.name === 'AbortError') {
            if (streamingResponseRef.current) {
              const sanitizedPartial = sanitizeMarkdown(
                streamingResponseRef.current,
              );
              dispatch(updateLastAssistantMessage(sanitizedPartial));
              dispatch(setChStatus('succeeded'));
            } else {
              dispatch(setChStatus('idle'));
            }
          } else {
            dispatch(setChStatus('failed'));
            toast.error(
              error instanceof Error
                ? error.message
                : 'Failed to process request. Please try again.',
            );
          }
          streamingResponseRef.current = '';
        }
      }
    });
  };

  // Auto-submit effect: when question is set from dictation, trigger search
  React.useEffect(() => {
    if (
      pendingDictationRef.current &&
      question === pendingDictationRef.current
    ) {
      const transcript = pendingDictationRef.current;
      pendingDictationRef.current = null;
      // Trigger search with the transcript
      setTimeout(() => {
        const fakeEvent = { preventDefault: () => {} } as React.FormEvent;
        handleSearchWithQuestion(transcript, fakeEvent);
      }, 100);
    }
  }, [question]);

  const handleSearch = async (event: React.FormEvent | React.KeyboardEvent) => {
    event.preventDefault();
    if (!question) return;

    // Check if we should use multi-file ask AI
    const useMultiFile = selectedFiles.length > 0;

    // For multi-file, we need teamId and at least one file
    if (useMultiFile && (!teamId || selectedFiles.length === 0)) {
      toast.error('Please select at least one file to analyze');
      return;
    }

    // For single-file, we need fileId
    if (!useMultiFile && !fileId) {
      return;
    }

    if (showLimitReached) {
      guardLimits('askAI', () => {});
      return;
    }

    const currentQuestion = question.trim();
    setQuestion('');
    setIsMultiline(false);

    guardLimits('askAI', async () => {
      // Clear any previous error status
      dispatch(clearChatError());

      // Track Ask AI chat
      let chatType: AskAIChatType;
      if (useMultiFile) {
        chatType = 'multifile';
      } else if (isVideoMode) {
        chatType = 'video';
      } else {
        chatType = 'search';
      }

      dispatch(
        trackAskAIChat({
          chatType,
          question: currentQuestion,
          fileCount: useMultiFile ? selectedFiles.length : 1,
          isVideoMode,
          ocrEnabled,
          sessionId,
          isNewSession: !sessionId,
        }),
      );

      if (useMultiFile) {
        // Handle multi-file question using regular API
        // Add user message to conversation immediately
        dispatch(
          addUserMessageToChat({ role: 'user', content: currentQuestion }),
        );

        // Set status to pending
        dispatch(setChStatus('pending'));

        try {
          const response = await askMultiFileSummarizedQuestion(teamId!, {
            fileIds: selectedFiles,
            question: currentQuestion,
            sessionId: sessionId || undefined,
          });

          if (response.success && response.data) {
            // Add assistant response to conversation
            dispatch(
              addAssistantMessageToChat({
                role: 'assistant',
                content: response.data.answer.content,
              }),
            );

            // Update session ID
            dispatch(setChatSessionId(response.data.sessionId));

            // Set status to succeeded
            dispatch(setChStatus('succeeded'));
          } else {
            throw new Error('Failed to get response');
          }
        } catch (error) {
          console.error('Failed to analyze recordings:', error);
          dispatch(setChStatus('failed'));
          toast.error(
            error instanceof Error
              ? error.message
              : 'Failed to process request. Please try again.',
          );
        }
      } else {
        // Handle single-file question with streaming
        if (!fileId || !teamId) return;

        // Add user message to conversation immediately
        dispatch(
          addUserMessageToChat({ role: 'user', content: currentQuestion }),
        );

        // Set status to pending
        dispatch(setChStatus('pending'));

        // Add an empty assistant message that will be updated during streaming
        dispatch(
          addAssistantMessageToChat({
            role: 'assistant',
            content: '',
          }),
        );

        // Clear previous streaming response
        streamingResponseRef.current = '';

        // Create AbortController for cancellation
        abortControllerRef.current = new AbortController();

        try {
          // Determine media analysis options
          let mediaAnalysisOptions: any;
          if (isVideoMode) {
            mediaAnalysisOptions = {
              video: {
                segments: [],
              },
            };
          } else if (ocrEnabled) {
            mediaAnalysisOptions = {
              ocr: {
                enabled: true,
              },
            };
          }

          // Use streaming endpoint for real-time responses
          const { sessionId: newSessionId } = await askSingleFileQuestionStream(
            teamId,
            fileId,
            currentQuestion,
            sessionId || undefined,
            (accumulatedText: string) => {
              // Store in ref so we can save partial response if aborted
              streamingResponseRef.current = accumulatedText;

              // Update the last assistant message with streaming content
              dispatch(updateLastAssistantMessage(accumulatedText));

              // Keep status as 'pending' during streaming to enable auto-scroll
              // Status will be set to 'succeeded' after streaming completes
            },
            {
              signal: abortControllerRef.current.signal,
              mediaAnalysisOptions,
            },
          );

          // Clear the abort controller after successful completion
          abortControllerRef.current = null;

          // Get the final response
          const finalResponse = streamingResponseRef.current;
          streamingResponseRef.current = '';

          // Sanitize the complete response (this happens after streaming is done)
          const sanitizedCompletion = sanitizeMarkdown(finalResponse);

          // Update with sanitized content only if it's different
          if (sanitizedCompletion !== finalResponse) {
            dispatch(updateLastAssistantMessage(sanitizedCompletion));
          }

          // Store the session ID for conversation continuity
          dispatch(setChatSessionId(newSessionId));

          // Set status to succeeded after streaming completes
          dispatch(setChStatus('succeeded'));
        } catch (error) {
          console.error('Failed to analyze recording:', error);

          // Clear the abort controller
          abortControllerRef.current = null;

          // Check if error was due to abort
          if (error instanceof Error && error.name === 'AbortError') {
            // User cancelled - save partial response if available
            if (streamingResponseRef.current) {
              const sanitizedPartial = sanitizeMarkdown(
                streamingResponseRef.current,
              );
              dispatch(updateLastAssistantMessage(sanitizedPartial));
              dispatch(setChStatus('succeeded'));
            } else {
              // No partial response - remove the empty assistant message
              // Since we added an empty message at the start, we need to handle this
              dispatch(setChStatus('idle'));
            }
            console.log('Request was cancelled by user');
          } else {
            dispatch(setChStatus('failed'));
            toast.error(
              error instanceof Error
                ? error.message
                : 'Failed to process request. Please try again.',
            );
          }

          // Clear the streaming ref
          streamingResponseRef.current = '';
        }
      }
      // Auto-scroll is now handled by useAutoScroll hook
    });
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSearch(event);
    }
  };

  // Auto-resize the container based on textarea content
  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    handleInputChange(e);
    // Auto-resize textarea
    e.target.style.height = 'auto';
    const newHeight = Math.min(e.target.scrollHeight, 150);
    e.target.style.height = `${newHeight}px`;
    setIsMultiline(newHeight > 40);
  };

  return (
    <TooltipProvider>
      <div className="flex flex-col w-full relative">
        <form onSubmit={handleSearch} className="w-full">
          <div
            className={cn(
              'bg-white dark:bg-background border shadow-sm',
              'transition-all duration-300 ease-in-out',
              'rounded-[20px]', // Matching the outer card radius
              isFocused
                ? 'border-primary shadow-md'
                : 'border-border shadow-sm',
              noAudio &&
                !isVideoMode &&
                !ocrEnabled &&
                selectedFiles.length === 0
                ? 'opacity-70'
                : 'opacity-100',
              // When video mode, OCR is active, or files are selected, adjust layout to keep controls at bottom
              isVideoMode || ocrEnabled || selectedFiles.length > 0
                ? 'p-1.5 flex flex-col gap-1.5'
                : 'px-1.5 py-1 flex items-end gap-1',
            )}
          >
            {/* Textarea - shows above when video mode, OCR is active, or files are selected */}
            {(isVideoMode || ocrEnabled || selectedFiles.length > 0) && (
              <div className="w-full px-1 py-1.5 animate-in slide-in-from-top-2 fade-in duration-300">
                <Textarea
                  ref={textareaRef}
                  className={cn(
                    'w-full min-h-[24px] max-h-[150px] resize-none border-0 px-2 py-1',
                    'text-sm leading-6 placeholder:text-muted-foreground',
                    'focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent',
                    'overflow-y-auto scrollbar-thin',
                  )}
                  placeholder={getPlaceholderText()}
                  aria-label="ask ai"
                  value={question}
                  onChange={handleTextareaChange}
                  onKeyDown={handleKeyDown}
                  onFocus={(e) => {
                    setIsFocused(true);
                    // Ensure proper height on focus
                    e.target.style.height = 'auto';
                    e.target.style.height = `${Math.min(e.target.scrollHeight, 150)}px`;
                  }}
                  onBlur={() => setIsFocused(false)}
                  disabled={
                    status === 'pending' ||
                    (noAudio && !isVideoMode && !ocrEnabled)
                  }
                  rows={1}
                />
              </div>
            )}

            {/* Main row with settings, input/chip, and send button */}
            <div
              className={cn(
                'flex gap-1 w-full',
                isMultiline ? 'items-end pb-0.5' : 'items-center',
              )}
            >
              {/* Left side - Settings button */}
              <div className="relative">
                <Popover open={settingsOpen} onOpenChange={setSettingsOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 rounded-full hover:bg-accent flex-shrink-0"
                    >
                      <Plus className="h-4 w-4 text-foreground" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    side="top"
                    align="start"
                    sideOffset={8}
                    alignOffset={0}
                    collisionPadding={16}
                    className="p-0 w-80 border-0 shadow-none"
                  >
                    <div className="w-80 max-h-96 overflow-hidden bg-popover border border-border rounded-md shadow-md">
                      <ScrollArea className="max-h-96">
                        <div className="py-2">
                          {/* Add Context button */}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              setShowFileSelector(!showFileSelector);
                              setSettingsOpen(false);
                            }}
                            className="w-full text-left flex items-center gap-3 px-6 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                          >
                            <Plus className="h-4 w-4" />
                            <span className="text-sm">
                              {t('chat.ask.addFiles', 'Add Context')}
                            </span>
                            {selectedFiles.length > 0 && (
                              <span className="ml-auto text-xs bg-primary text-primary-foreground rounded-full px-2 py-0.5">
                                {selectedFiles.length}
                              </span>
                            )}
                          </button>

                          {/* Separator */}
                          {hasVideo && (
                            <div className="my-1 border-t border-gray-200 dark:border-gray-700" />
                          )}

                          {/* Video mode toggle (only show if video exists) */}
                          {hasVideo && (
                            <>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  if (selectedFiles.length === 0) {
                                    handleVideoModeToggle();
                                  }
                                }}
                                className={cn(
                                  'w-full text-left flex justify-between items-center gap-3 px-6 py-2.5 transition-colors',
                                  selectedFiles.length === 0 &&
                                    'hover:bg-gray-50 dark:hover:bg-gray-800',
                                  selectedFiles.length > 0 &&
                                    'opacity-50 cursor-not-allowed',
                                )}
                              >
                                <div className="flex items-center gap-2">
                                  <span className="text-sm">
                                    {t(
                                      'documentGeneration.menuItem.videoAnalysis',
                                    )}
                                  </span>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-4 w-4 p-0 hover:bg-transparent"
                                    onClick={(e) =>
                                      handleInfoClick(
                                        e,
                                        t(
                                          'documentGeneration.menuItem.videoAnalysis',
                                          'Video Analysis',
                                        ),
                                        t(
                                          'documentGeneration.infoDialog.videoAnalysis',
                                          'The video analyzer watches every frame of your video and analyzes the visual content. It can identify colors, extract text from the screen, and determine which frames are most important for screenshots. This feature is perfect for capturing key visual information from your videos.',
                                        ),
                                        'videoAnalysis',
                                      )
                                    }
                                  >
                                    <Info className="h-3 w-3 text-muted-foreground" />
                                  </Button>
                                </div>
                                <Switch
                                  checked={isVideoMode}
                                  disabled={selectedFiles.length > 0}
                                  onClick={(e) => e.stopPropagation()}
                                  onCheckedChange={handleVideoModeToggle}
                                />
                              </button>

                              {/* OCR toggle (only show if video exists) */}
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  if (selectedFiles.length === 0) {
                                    handleOcrToggle();
                                  }
                                }}
                                className={cn(
                                  'w-full text-left flex justify-between items-center gap-3 px-6 py-2.5 transition-colors',
                                  selectedFiles.length === 0 &&
                                    'hover:bg-gray-50 dark:hover:bg-gray-800',
                                  selectedFiles.length > 0 &&
                                    'opacity-50 cursor-not-allowed',
                                )}
                              >
                                <div className="flex items-center gap-2">
                                  <span className="text-sm">
                                    {t(
                                      'documentGeneration.menuItem.ocr',
                                      'OCR',
                                    )}
                                  </span>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-4 w-4 p-0 hover:bg-transparent"
                                    onClick={(e) =>
                                      handleInfoClick(
                                        e,
                                        t(
                                          'documentGeneration.menuItem.ocr',
                                          'OCR',
                                        ),
                                        t(
                                          'documentGeneration.infoDialog.ocr',
                                          'OCR (Optical Character Recognition) extracts all text displayed in your video frames. This feature analyzes the visual content to capture text from slides, documents, UI elements, and any other on-screen text, making it searchable and available for content generation.',
                                        ),
                                        'ocr',
                                      )
                                    }
                                  >
                                    <Info className="h-3 w-3 text-muted-foreground" />
                                  </Button>
                                </div>
                                <Switch
                                  checked={ocrEnabled}
                                  disabled={selectedFiles.length > 0}
                                  onClick={(e) => e.stopPropagation()}
                                  onCheckedChange={handleOcrToggle}
                                />
                              </button>

                              {/* Separator */}
                              <div className="my-1 border-t border-gray-200 dark:border-gray-700" />
                            </>
                          )}

                          {/* Language selection - opens a separate popover */}
                          <button
                            type="button"
                            onClick={() => {
                              setLanguageOpen(true);
                            }}
                            className="w-full text-left flex items-center justify-between gap-3 px-6 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                          >
                            <span className="text-sm">
                              {t(
                                'documentGeneration.menuItem.outputLanguage',
                                'Output Language',
                              )}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {currentLanguageName}
                            </span>
                          </button>
                        </div>
                      </ScrollArea>
                    </div>
                  </PopoverContent>
                </Popover>

                {/* Language Selector Popover - opens from settings menu */}
                <Popover open={languageOpen} onOpenChange={setLanguageOpen}>
                  <PopoverTrigger asChild>
                    <span />
                  </PopoverTrigger>
                  <PopoverContent
                    side="right"
                    align="start"
                    sideOffset={8}
                    alignOffset={0}
                    collisionPadding={16}
                    className="p-0 w-48 border-0 shadow-none"
                  >
                    <div className="w-48 max-h-80 overflow-hidden bg-popover border border-border rounded-md shadow-md">
                      {/* Language options - minimal, no header */}
                      <ScrollArea className="max-h-80">
                        <div className="p-1">
                          {OUTPUT_LANGUAGES.map((lang) => (
                            <button
                              key={lang.code}
                              type="button"
                              onClick={() => handleLanguageSelect(lang.code)}
                              className={cn(
                                'w-full text-left px-2 py-1.5 rounded-sm text-sm transition-colors',
                                'hover:bg-accent hover:text-accent-foreground',
                                lang.code === outputLanguage &&
                                  'bg-accent text-accent-foreground font-medium',
                              )}
                            >
                              {lang.name}
                            </button>
                          ))}
                        </div>
                      </ScrollArea>
                    </div>
                  </PopoverContent>
                </Popover>

                {/* File Selector - positioned using Popover */}
                <Popover
                  open={showFileSelector}
                  onOpenChange={setShowFileSelector}
                >
                  <PopoverTrigger asChild>
                    <span />
                  </PopoverTrigger>
                  <PopoverContent
                    side="top"
                    align="start"
                    sideOffset={8}
                    alignOffset={0}
                    collisionPadding={16}
                    className="p-0 w-auto border-0 shadow-none"
                  >
                    <FileSelector
                      selectedFiles={selectedFiles}
                      onFileSelect={handleFileSelect}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Middle - Either textarea or feature chips */}
              {!isVideoMode && !ocrEnabled && selectedFiles.length === 0 && (
                // Normal textarea when not in video mode
                <Textarea
                  ref={textareaRef}
                  className={cn(
                    'flex-1 min-h-[24px] max-h-[150px] resize-none border-0 pl-1 pr-2 py-1',
                    'text-sm leading-6 placeholder:text-muted-foreground',
                    'focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent',
                    'overflow-y-auto scrollbar-thin',
                  )}
                  placeholder={getPlaceholderText()}
                  aria-label="ask ai"
                  value={question}
                  onChange={handleTextareaChange}
                  onKeyDown={handleKeyDown}
                  onFocus={(e) => {
                    setIsFocused(true);
                    // Ensure proper height on focus
                    e.target.style.height = 'auto';
                    e.target.style.height = `${Math.min(e.target.scrollHeight, 150)}px`;
                  }}
                  onBlur={() => setIsFocused(false)}
                  disabled={
                    status === 'pending' ||
                    (noAudio && !isVideoMode && !ocrEnabled)
                  }
                  rows={1}
                />
              )}
              {isVideoMode && (
                // Video analyzer chip when in video mode
                <div className="flex-1 flex items-center">
                  <FeatureToggleChip
                    icon={<Video className="h-3 w-3" strokeWidth={2} />}
                    label="Analyzing video"
                    onClick={() => dispatch(setVideoMode(false))}
                  />
                </div>
              )}
              {ocrEnabled && (
                // OCR chip when OCR is enabled
                <div className="flex-1 flex items-center">
                  <FeatureToggleChip
                    icon={<ScanText className="h-3 w-3" strokeWidth={2} />}
                    label="Extracting text"
                    onClick={() => dispatch(setOcrMode(false))}
                  />
                </div>
              )}
              {selectedFiles.length > 0 && (
                // Single file chip showing first file name and count
                <div className="flex-1 flex items-center">
                  <FileChip
                    fileCount={selectedFiles.length}
                    fileName={cachedFiles[0]?.name}
                    onClick={() => setShowFileSelector(!showFileSelector)}
                    onClear={() => {
                      selectedFiles.forEach((selectedFileId) =>
                        dispatch(removeSelectedFile(selectedFileId)),
                      );
                      setCachedFiles([]);
                    }}
                  />
                </div>
              )}

              {/* Right side - Send/Dictation button */}
              <div
                className={cn(
                  'flex gap-1 flex-shrink-0',
                  isMultiline ? 'items-end' : 'items-center',
                )}
              >
                {(() => {
                  const hasText = question.trim();
                  const isLoading = status === 'pending';
                  const isInputDisabled =
                    noAudio && !isVideoMode && !ocrEnabled;

                  // Show dictation button when input is empty (like ChatGPT)
                  if (
                    !hasText &&
                    !isLoading &&
                    !isRecordingLocal &&
                    !isDictationProcessing
                  ) {
                    return (
                      <Button
                        type="button"
                        onClick={handleStartDictation}
                        size="icon"
                        variant="ghost"
                        disabled={isInputDisabled}
                        className="h-7 w-7 rounded-full transition-all duration-300 hover:bg-muted/50"
                        title={t('dictation.tooltip', 'Dictate your message')}
                      >
                        <Mic className="h-4 w-4 text-muted-foreground" />
                      </Button>
                    );
                  }

                  // Show stop button while recording
                  if (isRecordingLocal) {
                    return (
                      <Button
                        type="button"
                        onClick={handleStopDictation}
                        size="icon"
                        variant="destructive"
                        className="h-7 w-7 rounded-full transition-all duration-300"
                        title={t('dictation.stop', 'Stop recording')}
                      >
                        <Stop className="h-4 w-4 text-white" weight="fill" />
                      </Button>
                    );
                  }

                  // Show processing spinner during dictation processing
                  if (isDictationProcessing) {
                    return (
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        disabled
                        className="h-7 w-7 rounded-full transition-all duration-300"
                      >
                        <Loader className="h-4 w-4 animate-spin text-muted-foreground" />
                      </Button>
                    );
                  }

                  // Show loading spinner during chat processing
                  if (isLoading) {
                    return (
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        disabled
                        className="h-7 w-7 rounded-full transition-all duration-300 bg-muted hover:bg-muted/80"
                      >
                        <Loader className="w-4 h-4 animate-spin text-muted-foreground" />
                      </Button>
                    );
                  }

                  // Show send button when there's text
                  return (
                    <Button
                      type="submit"
                      size="icon"
                      disabled={!hasText || isInputDisabled}
                      variant="default"
                      className={cn(
                        'h-7 w-7 rounded-full transition-all duration-300',
                        'bg-primary hover:bg-primary/90 hover:scale-105',
                      )}
                    >
                      <ArrowUp
                        className={cn(
                          'h-4 w-4 transition-all duration-300 text-primary-foreground',
                          'hover:-translate-y-0.5',
                        )}
                      />
                    </Button>
                  );
                })()}
              </div>
            </div>
          </div>
        </form>

        {/* Info Dialog */}
        <ActionDialog
          open={infoDialogOpen}
          onClose={handleInfoDialogClose}
          title={infoDialogContent.title}
          maxWidth="sm"
          actions={[
            ...(infoDialogContent.featureType &&
            ((infoDialogContent.featureType === 'videoAnalysis' &&
              !isVideoMode) ||
              (infoDialogContent.featureType === 'ocr' && !ocrEnabled))
              ? [
                  {
                    key: 'turn-on',
                    label: 'Turn On',
                    onClick: handleTurnOnFeature,
                    variant: 'contained' as const,
                    color: 'primary' as const,
                  },
                ]
              : []),
          ]}
        >
          <p className="text-sm text-muted-foreground">
            {infoDialogContent.content || 'Information coming soon...'}
          </p>
        </ActionDialog>

        <ActionDialog
          open={showLongVideoDialog}
          title={t('chat.ask.longVideoError.title')}
          actions={[
            {
              key: 'ok',
              label: 'OK',
              onClick: () => setShowLongVideoDialog(false),
              color: 'primary',
              variant: 'contained',
            },
          ]}
        >
          <p className="text-sm text-muted-foreground">
            {t('chat.ask.longVideoError.message')}
          </p>
        </ActionDialog>
      </div>
    </TooltipProvider>
  );
});

CustomizedInputBase.displayName = 'ChatInput';

export default CustomizedInputBase;
